/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useMemo } from 'react';
import {
  Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, tableCellClasses,
} from '@mui/material';
import { styled } from '@mui/system';
import get from 'lodash.get';
import { useFormState } from 'react-final-form';
import ExternalLink from '../../../Components/ExternalLink';
import { shouldHideStep } from '../helper';

const selectableFieldTypes = ['select', 'radio', 'autocomplete', 'radioWHeadline', 'radioWBox'];

const getFieldOptions = (fieldOptions = []) => fieldOptions.reduce((acc, option) => {
  if (!option.groupTitle) {
    acc.push(option);
    return acc;
  }
  acc.push(...option.options);
  return acc;
}, []);

const sortSummaryFields = (fields = []) => fields
  .filter((field) => !field?.hideInSummary)
  .slice()
  .sort((a, b) => {
    if (a.summarySequenceId !== undefined && b.summarySequenceId !== undefined) {
      return a.summarySequenceId - b.summarySequenceId;
    }
    if (a.summarySequenceId !== undefined) return -1;
    if (b.summarySequenceId !== undefined) return 1;
    return 0;
  });

const formatValue = (formValues, field) => {
  const value = get(formValues, field.name);
  if (field.type === 'checkboxWHeadline' || field.type === 'checkbox') {
    return value === true ? field.trueLabelInSummary : '';
  }
  if (selectableFieldTypes.includes(field.type)) {
    const options = getFieldOptions(field.options);
    const option = options.find(({ value: optionValue }) => optionValue === value);
    return option?.nameInSummary || option?.name;
  }
  return value;
};

const formatLink = (formValues, field) => {
  const value = get(formValues, field.name);
  if (selectableFieldTypes.includes(field.type)) {
    const option = getFieldOptions(field.options).find(({ value: optionValue }) => optionValue === value);
    if (!option.linkTextInSummary) return null;
    return (
      <TableCell align="right">
        <ExternalLink href={option.linkInSummary}>
          {option.linkTextInSummary}
        </ExternalLink>
      </TableCell>
    );
  }
  return null;
};

export default function DynamicSummary({
  inviteForm,
  inviteValues,
  withLinks = false,
  hideHeadline = false,
}) {
  const showTicketDownload = inviteForm?.frontendSettings?.enabledTicketDownloadUrl === true;
  const formState = useFormState();
  const visibleSummaryElements = useMemo(() => {
    if (!inviteForm?.elements?.length) return [];
    return inviteForm.elements
      .filter(({ type, params }) => ['mainParticipantPrompt', 'participantPrompt'].includes(type) && !params?.hideInSummary
       && !shouldHideStep(formState.values, params?.hideConditions));
  }, [formState.values, inviteForm?.elements]);

  const inviteFormAttributeMap = useMemo(
    () => new Map((inviteForm?.attributes || []).map((attr) => [attr.code, attr])),
    [inviteForm?.attributes],
  );

  const getParticipantPromptFields = (params = {}) => {
    if (params?.customFieldDefinition?.length) {
      return params.customFieldDefinition;
    }

    const fields = [
      { name: 'forename', label: 'Vorname' },
      { name: 'name', label: 'Nachname' },
      { name: 'email', label: params.attendeeEmailLabel || 'E-Mail (an diese E-Mail wird die Einlasskarte gesendet)', type: 'email' },
    ];

    for (const attrCode of (params.attributes || [])) {
      const attr = inviteFormAttributeMap.get(attrCode);
      if (!attr) continue;
      fields.push({
        name: `customAttributes.${attrCode}`,
        label: attr.name,
        type: attr.type,
        options: attr.options?.map(({ label, value }) => ({ name: label, value })),
      });
    }
    return fields;
  };

  const renderSummaryRows = (values, fields, prefix = '') => {
    const sortedFields = sortSummaryFields(fields);
    return sortedFields.map((field) => {
      const fieldName = prefix ? `${prefix}.${field.name}` : field.name;
      const value = get(values, fieldName);
      if (value === undefined || value === null) return null;
      const mappedField = { ...field, name: fieldName };
      return (
        <FormattedTableRow key={fieldName}>
          <TableCell component="th" scope="row" style={{ fontWeight: 'bold' }}>
            {field.headlineInSummary || field.label}
          </TableCell>
          <TableCell align="right">{formatValue(values, mappedField)}</TableCell>
          {withLinks && formatLink(values, mappedField)}
        </FormattedTableRow>
      );
    });
  };

  return (
    <Grid container direction="row" justifyContent="center">
      <Grid item xs={12} md={6}>
        {visibleSummaryElements.map((formElement, formElementIndex) => {
          const sortedFields = sortSummaryFields(formElement.params?.customFieldDefinition || []);
          if (formElement.type === 'mainParticipantPrompt') {
            return (
              <React.Fragment key={`${formElement.type}-${formElementIndex}`}>
                <Typography sx={{ textAlign: 'center', mt: 3, mb: 3 }} variant="h6">
                  {formElement.title}
                </Typography>
                <TableContainer component={Paper}>
                  <Table
                    sx={{ [`& .${tableCellClasses.root}`]: { borderBottom: 'none' } }}
                  >
                    <TableBody>
                      {renderSummaryRows(inviteValues, sortedFields)}
                    </TableBody>
                  </Table>
                </TableContainer>
              </React.Fragment>
            );
          }

          const attendees = inviteValues?.attendees || [];
          const participantFields = getParticipantPromptFields(formElement.params);
          return (
            <React.Fragment key={`${formElement.type}-${formElementIndex}`}>
              {formElement.title && !hideHeadline ? (
                <Typography sx={{ textAlign: 'center', mt: 3, mb: 3 }} variant="h6">
                  {formElement.title}
                </Typography>
              ) : null}
              {attendees.map((attendee, attendeeIndex) => (
                <React.Fragment key={`${formElement.type}-${formElementIndex}-attendee-${attendee.id || attendee.tempId || attendeeIndex}`}>
                  <Typography sx={{ textAlign: 'center', mt: 3, mb: 3 }} variant="h6">
                    {(formElement.params?.participantDescription || 'Gast')}
                    {' '}
                    {attendeeIndex + 1}
                  </Typography>
                  <Paper>
                    <TableContainer>
                      <Table
                        sx={{ [`& .${tableCellClasses.root}`]: { borderBottom: 'none' } }}
                      >
                        <TableBody>
                          {renderSummaryRows(inviteValues, participantFields, `attendees.${attendeeIndex}`)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {showTicketDownload && attendee.ticketDownloadUrl && (
                      <Box sx={{
                        display: 'flex', justifyContent: 'center', p: 2, borderTop: '1px solid', borderColor: 'divider',
                      }}
                      >
                        <Button
                          variant="contained"
                          href={attendee.ticketDownloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ticket herunterladen
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </Grid>
    </Grid>
  );
}

const FormattedTableRow = styled(TableRow)`
  &:last-child td, &:last-child th {
    border: 0
  }
`;
